import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

export const ESTADOS = ["nuevo", "contactado", "en diagnóstico", "cerrado"];

const BADGE = {
  nuevo: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  contactado: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  "en diagnóstico": "bg-rc-forest/30 text-rc-sage border-rc-forest/40",
  cerrado: "bg-stone-600/20 text-stone-400 border-stone-600/30",
};

export const EstadoBadge = ({ estado }) => (
  <span className={`inline-block text-xs font-mono px-2.5 py-1 rounded-full border whitespace-nowrap ${BADGE[estado] || BADGE.nuevo}`}>
    {estado || "nuevo"}
  </span>
);

export const StatusManager = ({ collection, row, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [estado, setEstado] = useState(row.estado || "nuevo");
  const [notas, setNotas] = useState(row.notas || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch(`/admin/${collection}/${row.id}`, { estado, notas });
      toast.success("Registro actualizado");
      onSaved?.();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`status-manage-${collection}-${row.id}`}
          className="inline-flex items-center gap-1.5 text-xs text-rc-sage hover:text-rc-forestlight transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Gestionar
        </button>
      </DialogTrigger>
      <DialogContent className="bg-rc-card border-rc-border text-stone-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-light">{row.nombre || row.curso_titulo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-stone-300">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger data-testid={`status-select-${row.id}`} className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-rc-card border-rc-border text-stone-100">
                {ESTADOS.map((e) => (
                  <SelectItem key={e} value={e} className="focus:bg-rc-forest/30 focus:text-stone-100">{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-stone-300">Notas internas</Label>
            <Textarea data-testid={`status-notas-${row.id}`} rows={4} value={notas} onChange={(e) => setNotas(e.target.value)}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
          <button onClick={save} disabled={loading} data-testid={`status-save-${row.id}`}
            className="w-full py-3 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors disabled:opacity-60">
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
